import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AdaptiveImage } from "@/components/ui/adaptive-image";

export interface ProductImage {
  id: string;
  src: string;
  alt: string;
}

interface ProductImageSwiperProps {
  images: ProductImage[];
  activeImageId?: string;
  className?: string;
}

const ProductImageSwiper = ({ images, activeImageId, className }: ProductImageSwiperProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => {
    emblaApi?.scrollTo(index);
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Navigate to active image when it changes (variation selection)
  useEffect(() => {
    if (!emblaApi || !activeImageId) return;
    const index = images.findIndex((img) => img.id === activeImageId);
    if (index !== -1 && index !== selectedIndex) {
      emblaApi.scrollTo(index);
    }
  }, [activeImageId, emblaApi, images, selectedIndex]);

  if (images.length === 0) return null;

  // If only one image, show it without swiper
  if (images.length === 1) {
    return (
      <div className={cn("relative", className)}>
        <div className="absolute inset-0 bg-pastel-green organic-shape opacity-20 -z-10 scale-110" />
        <AdaptiveImage
          src={images[0].src}
          alt={images[0].alt}
          containerClassName="aspect-square rounded-3xl bg-white border border-border/50 shadow-card"
        />
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <div className="absolute inset-0 bg-pastel-green organic-shape opacity-20 -z-10 scale-110" />
      
      {/* Carousel container with arrows */}
      <div className="relative group">
        {/* Main carousel */}
        <div 
          ref={emblaRef} 
          className="overflow-hidden rounded-3xl bg-white border border-border/50 shadow-card"
        >
          <div className="flex">
            {images.map((image) => (
              <div 
                key={image.id} 
                className="flex-[0_0_100%] min-w-0"
              >
                <AdaptiveImage
                  src={image.src}
                  alt={image.alt}
                  containerClassName="aspect-square"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation arrows - inside carousel container for correct centering */}
        <Button
          variant="ghost"
          size="icon"
          onClick={scrollPrev}
          className={cn(
            "absolute left-3 top-1/2 -translate-y-1/2 z-10",
            "h-10 w-10 rounded-full",
            "bg-white/80 backdrop-blur-sm shadow-md",
            "hover:bg-white hover:scale-105",
            "transition-all duration-200",
            "sm:opacity-0 sm:group-hover:opacity-100"
          )}
        >
          <ChevronLeft className="h-5 w-5 text-foreground" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={scrollNext}
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 z-10",
            "h-10 w-10 rounded-full",
            "bg-white/80 backdrop-blur-sm shadow-md",
            "hover:bg-white hover:scale-105",
            "transition-all duration-200",
            "sm:opacity-0 sm:group-hover:opacity-100"
          )}
        >
          <ChevronRight className="h-5 w-5 text-foreground" />
        </Button>
      </div>

      {/* Dot indicators - outside carousel container */}
      <div className="flex justify-center gap-2 mt-4">
        {images.map((image, index) => (
          <button
            key={image.id}
            onClick={() => scrollTo(index)}
            className={cn(
              "w-2 h-2 rounded-full transition-all duration-200",
              index === selectedIndex
                ? "bg-primary w-6"
                : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
            )}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export { ProductImageSwiper };
